import { PhoneService } from '@superdispatch/phones';
import { z } from 'zod';

// fleet_type values are the AtoB onboarding contract; `heavy` is the product
// default (NPR-164).
export const FLEET_TYPE_VALUES = ['heavy', 'mixed', 'light'] as const;

export type SuperCardFleetType = (typeof FLEET_TYPE_VALUES)[number];

export const DEFAULT_FLEET_TYPE: SuperCardFleetType = 'heavy';

// Coerce numeric strings so a stringly-typed count still parses.
function toNumber(value: unknown): unknown {
  return typeof value === 'string' && value.trim() ? Number(value) : value;
}

const FLEET_TYPE_LABELS: Record<SuperCardFleetType, string> = {
  light: 'Light (Fleet operating local routes, unleaded and/or diesel)',
  mixed: 'Mixed (Fleet operating both light and heavy vehicles)',
  heavy: 'Heavy (Trucking fleet, diesel only)',
};

export const FLEET_TYPE_OPTIONS = FLEET_TYPE_VALUES.map((value) => ({
  value,
  label: FLEET_TYPE_LABELS[value],
}));

// `dot_number` is the only optional field and must render without an
// "Optional" label.
//
// Formik turns '' into undefined before validating, so `.prefault('')` lets
// the required checks report their message instead of a type error.
export const superCardApplicationFormSchema = z.object({
  email: z
    .string()
    .min(1, 'This field is required')
    .pipe(z.email('Enter a valid email'))
    .prefault(''),
  contact_name: z.string().min(1, 'This field is required').prefault(''),
  phone_number: z
    .string()
    .trim()
    .min(1, 'This field is required')
    .superRefine(async (value, ctx) => {
      const message = await PhoneService.validate(value);

      if (message) {
        ctx.addIssue({ code: 'custom', message });
      }
    })
    .prefault(''),
  fleet_size: z
    .number('This field is required')
    .min(0, 'Value must be greater than or equal to 0'),
  fleet_type: z.enum(FLEET_TYPE_VALUES).default(DEFAULT_FLEET_TYPE),
  weekly_spend_estimate: z
    .number('This field is required')
    .min(0, 'Value must be greater than or equal to 0'),
  dot_number: z.string().optional(),
});

export type SuperCardApplicationFormValues = z.infer<
  typeof superCardApplicationFormSchema
>;

// Backend prefill payload shape (NPR-164/167). The Fleet Size field is
// prefilled from `fleet_size` and `weekly_spend_estimate` is `fleet_size` ×
// $3,000. The numbers fall back to 0, the same value the empty form starts
// with.
export type SuperCardApplicationPrefillDTO = z.infer<
  typeof superCardApplicationPrefillSchema
>;
export const superCardApplicationPrefillSchema = z.object({
  email: z.string().default(''),
  contact_name: z.string().default(''),
  phone_number: z.string().default(''),
  dot_number: z.string().default(''),
  fleet_size: z.preprocess(toNumber, z.number().default(0)),
  activated_driver_count: z.preprocess(toNumber, z.number().default(0)),
  weekly_spend_estimate: z.preprocess(toNumber, z.number().default(0)),
  fleet_type: z.enum(FLEET_TYPE_VALUES).default(DEFAULT_FLEET_TYPE),
});

export function getEmptySuperCardApplicationFormValues(): SuperCardApplicationFormValues {
  return {
    email: '',
    contact_name: '',
    phone_number: '',
    fleet_size: 0,
    fleet_type: DEFAULT_FLEET_TYPE,
    weekly_spend_estimate: 0,
    dot_number: '',
  };
}

export function getSuperCardApplicationFormValues(
  prefill?: SuperCardApplicationPrefillDTO | null,
): SuperCardApplicationFormValues {
  if (!prefill) {
    return getEmptySuperCardApplicationFormValues();
  }

  return {
    email: prefill.email,
    contact_name: prefill.contact_name,
    phone_number: prefill.phone_number,
    fleet_size: prefill.fleet_size,
    fleet_type: prefill.fleet_type,
    weekly_spend_estimate: prefill.weekly_spend_estimate,
    dot_number: prefill.dot_number,
  };
}

export interface SuperCardApplicationSubmitRequest {
  email: string;
  contact_name: string;
  phone_number: string;
  dot_number?: string;
  fleet_size: number;
  weekly_spend_estimate: number;
  fleet_type: SuperCardFleetType;
}

// Backend returns only the signup link; SuperCard state is refreshed separately.
export interface SuperCardApplicationSubmitResponse {
  signup_link: string;
}

export function toSuperCardApplicationSubmitRequest(
  values: SuperCardApplicationFormValues,
): SuperCardApplicationSubmitRequest {
  return {
    email: values.email,
    contact_name: values.contact_name,
    phone_number: values.phone_number,
    ...(values.dot_number ? { dot_number: values.dot_number } : {}),
    fleet_size: values.fleet_size,
    weekly_spend_estimate: values.weekly_spend_estimate,
    fleet_type: values.fleet_type,
  };
}
